import { useTenantConfig } from '@/api/hooks/useTenantConfig'
import { useTenantStore } from '@/stores/tenantStore'
import { formatShortDate } from '@/utils/dateFormatters'
import { Vertical } from '@gravitate-js/excalibrr'
import { Badge, Tabs, Typography } from 'antd'
import { InstancesGrid } from './components/InstancesGrid'
import { PartnersGrid } from './components/PartnersGrid'
import { TankReadingConfigsGrid } from './components/TankReadingConfigsGrid'
import { TenantAvatar } from './components/TenantAvatar'
import { TenantHealth } from './components/TenantHealth'

const { Title, Text } = Typography

// Crossroads purple -- every tab count badge uses it.
const TAB_BADGE_STYLE = { backgroundColor: '#8b5cf6' }

export function TenantConfiguration() {
  const { selectedTenant } = useTenantStore()
  const { data: config } = useTenantConfig()

  const items = [
    {
      key: 'partners',
      label: (
        <span>
          Partners{' '}
          <Badge
            count={config?.partners?.length || 0}
            style={TAB_BADGE_STYLE}
          />
        </span>
      ),
      children: <PartnersGrid />,
    },
    {
      key: 'tank-readings',
      label: (
        <span>
          Tank Readings{' '}
          <Badge
            count={config?.tank_reading_configs?.length || 0}
            style={TAB_BADGE_STYLE}
          />
        </span>
      ),
      children: <TankReadingConfigsGrid />,
    },
    {
      key: 'instances',
      label: (
        <span>
          System Instances{' '}
          <Badge
            count={config?.instances?.length || 0}
            style={TAB_BADGE_STYLE}
          />
        </span>
      ),
      children: <InstancesGrid />,
    },
    {
      key: 'health',
      label: 'Health',
      children: <TenantHealth tenantName={selectedTenant?.name} />,
    },
  ]

  const tabItems = items.map((item) => ({
    key: item.key,
    label: item.label,
    children: (
      <div style={{ height: '100%', display: 'flex', flex: 1 }}>
        {item.children}
      </div>
    ),
  }))

  if (!selectedTenant) {
    return (
      <Vertical
        bg='layout'
        style={{
          height: '100%',
          padding: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text type='secondary'>Please select a tenant to configure</Text>
      </Vertical>
    )
  }

  const createdDate = formatShortDate(config?.created)

  return (
    <Vertical
      className='mt-2'
      style={{
        overflow: 'visible',
      }}
    >
      <div
        style={{
          padding: 16,
          backgroundColor: 'var(--bg-2)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <TenantAvatar
          tenantName={selectedTenant.name}
          logoUrl={config?.logo_url}
          logoThumbUrl={config?.logo_thumb_url}
        />
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {selectedTenant.name}
          </Title>
          {createdDate && <Text type='secondary'>Created: {createdDate}</Text>}
          {selectedTenant.goid && (
            <div>
              <Text type='secondary'>GOID: {selectedTenant.goid}</Text>
            </div>
          )}
        </div>
      </div>

      <Tabs
        defaultActiveKey='partners'
        destroyInactiveTabPane={false}
        items={tabItems}
        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      />
    </Vertical>
  )
}
